import { type Component, type Page } from "../../types";
import { generateId } from "../../utils";
import { rebuildParentIds } from "../../utils/rebuildParentIds";
import { enhanceComponentsWithChildren } from "./editor-utils";
import {
  normalizePageCanvasSettings,
  toIdMap,
  typedObjectKeys,
} from "./editor-store-helpers";
import { buildPage } from "./page-slice-helpers";
import type {
  PageSlice,
  PageSliceState,
} from "./page-slice-types";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";
import { VersionIntegrationContext } from "./versioning-integration";

export type { PageSlice } from "./page-slice-types";

export function createPageSlice<TState extends PageSliceState>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): PageSlice {
  return {
    addPage: (pageData) => {
      const page = buildPage(pageData, get());

      if (get().versioningEnabled) {
        VersionIntegrationContext.getInstance().trackPageChange(
          "create",
          page.id,
          undefined,
          page,
          {
            description: `Created page: ${page.name}`,
          },
        );
      }

      set((state) => {
        state.pages.push(page);
        state.pageMap.set(page.id, page);
        state.pageComponents.set(page.id, []);
        state.pageHistoryMap.set(page.id, {
          past: [],
          present: [],
          future: [],
        });
        state.isModified = true;

        if (!state.currentPage || state.pages.length === 1) {
          state.currentPageId = page.id;
          state.currentPage = page;
          state.components = [];
          state.componentMap = new Map();
        }
      });

      return page;
    },

    switchToPage: (pageId) => {
      const state = get();
      const page = state.pageMap.get(pageId);
      if (!page) {
        return;
      }

      const previousPageId = state.currentPageId;
      const previousComponents = state.components;
      const previousHistory = state.history;

      set((draft) => {
        if (previousPageId) {
          draft.pageComponents.set(
            previousPageId,
            enhanceComponentsWithChildren([...previousComponents]),
          );
          draft.pageHistoryMap.set(previousPageId, { ...previousHistory });
        }

        draft.currentPageId = pageId;
        draft.currentPage = page;

        const pageComponents = draft.pageComponents.get(pageId) || [];
        const rebuiltPageComponents = rebuildParentIds(pageComponents);
        const enhancedPageComponents =
          enhanceComponentsWithChildren(rebuiltPageComponents);

        draft.components = enhancedPageComponents;
        draft.componentMap = toIdMap(enhancedPageComponents);
        draft.selectedComponents = [];
        draft.canvasSelected = false;

        const pageHistory = draft.pageHistoryMap.get(pageId);
        draft.history = pageHistory || {
          past: [],
          present: [...enhancedPageComponents],
          future: [],
        };

        const nextPageHistory = [...draft.pageHistory];
        const existingIndex = nextPageHistory.findIndex((id) => id === pageId);
        if (existingIndex >= 0) {
          nextPageHistory.splice(existingIndex, 1);
        }
        nextPageHistory.unshift(pageId);
        draft.pageHistory = nextPageHistory.slice(0, 10);
      });
    },

    deletePage: (pageId) => {
      const state = get();
      const page = state.pageMap.get(pageId);
      if (!page || page.isDefault || state.pages.length <= 1) {
        return;
      }

      if (state.versioningEnabled) {
        VersionIntegrationContext.getInstance().trackPageChange(
          "delete",
          pageId,
          page,
          undefined,
          {
            description: `Deleted page: ${page.name}`,
          },
        );
      }

      set((draft) => {
        draft.pages = draft.pages.filter((candidate) => candidate.id !== pageId);
        draft.pageMap.delete(pageId);
        draft.pageComponents.delete(pageId);
        draft.pageHistoryMap.delete(pageId);
        draft.pageHistory = draft.pageHistory.filter((id) => id !== pageId);
        draft.isModified = true;
      });

      if (state.currentPageId === pageId) {
        const defaultPage = get().pages.find((candidate) => candidate.isDefault);
        if (defaultPage) {
          get().switchToPage(defaultPage.id);
        }
      }
    },

    updatePage: (pageId, updates) => {
      const oldPage = get().pageMap.get(pageId);

      set((state) => {
        const page = state.pageMap.get(pageId);
        if (!page) {
          return;
        }

        if (state.versioningEnabled && oldPage) {
          const significantFields: (keyof typeof updates)[] = [
            "name",
            "description",
            "template",
            "permissions",
          ];
          const changedFields = typedObjectKeys(updates).filter(
            (key) =>
              significantFields.includes(key) &&
              updates[key] !== oldPage[key],
          );

          if (changedFields.length > 0) {
            const versionContext = VersionIntegrationContext.getInstance();
            changedFields.forEach((field) => {
              const fieldName = String(field);
              versionContext.trackPageChange(
                "update",
                pageId,
                oldPage[field],
                updates[field],
                {
                  field: fieldName,
                  description: `Updated ${fieldName} of page: ${oldPage.name}`,
                },
              );
            });
          }
        }

        const updatedPage: Page = {
          ...page,
          ...updates,
          navigation: {
            ...page.navigation,
            ...updates.navigation,
          },
          permissions: {
            ...page.permissions,
            ...updates.permissions,
          },
          canvasSettings: normalizePageCanvasSettings({
            ...page.canvasSettings,
            ...updates.canvasSettings,
          }),
          updatedAt: new Date(),
          lastModifiedAt: new Date(),
        };

        state.pageMap.set(pageId, updatedPage);
        const pageIndex = state.pages.findIndex((candidate) => candidate.id === pageId);
        if (pageIndex !== -1) {
          state.pages[pageIndex] = updatedPage;
        }

        if (state.currentPageId === pageId) {
          state.currentPage = updatedPage;
        }

        state.isModified = true;
      });
    },

    duplicatePage: (pageId, newName) => {
      const state = get();
      const sourcePage = state.pageMap.get(pageId);
      const sourceComponents = state.pageComponents.get(pageId) || [];

      if (!sourcePage) {
        throw new Error(`Page ${pageId} not found`);
      }

      const duplicatedPage: Page = {
        ...sourcePage,
        id: generateId("page"),
        name: newName || `${sourcePage.name} Copy`,
        order: state.pages.length,
        isDefault: false,
        canvasSettings: normalizePageCanvasSettings(sourcePage.canvasSettings),
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const duplicatedComponents = sourceComponents.map((component) => ({
        ...component,
        id: generateId("component"),
        pageId: duplicatedPage.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      }));

      set((draft) => {
        draft.pages.push(duplicatedPage);
        draft.pageMap.set(duplicatedPage.id, duplicatedPage);

        const enhancedComponents =
          enhanceComponentsWithChildren(duplicatedComponents);
        draft.pageComponents.set(duplicatedPage.id, enhancedComponents);
        draft.pageHistoryMap.set(duplicatedPage.id, {
          past: [],
          present: [...enhancedComponents],
          future: [],
        });
        draft.isModified = true;
      });

      return duplicatedPage;
    },

    reorderPages: (pageOrder) => {
      const currentState = get();
      const oldOrder = currentState.pages.map((page) => page.id);

      if (
        currentState.versioningEnabled &&
        JSON.stringify(oldOrder) !== JSON.stringify(pageOrder)
      ) {
        VersionIntegrationContext.getInstance().trackPageChange(
          "reorder",
          "pages",
          oldOrder,
          pageOrder,
          { description: "Reordered pages" },
        );
      }

      set((state) => {
        const reorderedPages: Page[] = [];
        pageOrder.forEach((pageId, index) => {
          const page = state.pageMap.get(pageId);
          if (!page) {
            return;
          }

          const reorderedPage: Page = {
            ...page,
            order: index,
            canvasSettings: normalizePageCanvasSettings(page.canvasSettings),
          };

          reorderedPages.push(reorderedPage);
          state.pageMap.set(pageId, reorderedPage);
        });

        state.pages = reorderedPages;
        state.isModified = true;
      });
    },

    movePage: (pageId, newOrder) => {
      set((state) => {
        const page = state.pageMap.get(pageId);
        if (!page) {
          return;
        }

        const updatedPage: Page = {
          ...page,
          order: newOrder,
          canvasSettings: normalizePageCanvasSettings(page.canvasSettings),
        };

        state.pageMap.set(pageId, updatedPage);

        const pageIndex = state.pages.findIndex((candidate) => candidate.id === pageId);
        if (pageIndex !== -1) {
          state.pages[pageIndex] = updatedPage;
        }

        state.pages.sort((left, right) => left.order - right.order);
        state.isModified = true;
      });
    },

    moveComponentToPage: (componentId, targetPageId, position) => {
      const state = get();
      const component = state.getComponent(componentId);
      if (!component || !state.pageMap.get(targetPageId)) {
        return;
      }

      const currentPageId = state.currentPageId || "";
      const currentPageComponents =
        state.pageComponents.get(currentPageId) || [];
      const updatedCurrentComponents = currentPageComponents.filter(
        (candidate) => candidate.id !== componentId,
      );
      const targetPageComponents =
        state.pageComponents.get(targetPageId) || [];
      const movedComponent: Component = {
        ...component,
        pageId: targetPageId,
        position: position || component.position,
        updatedAt: new Date(),
      };

      set((draft) => {
        draft.pageComponents.set(currentPageId, updatedCurrentComponents);
        draft.pageComponents.set(
          targetPageId,
          enhanceComponentsWithChildren([
            ...targetPageComponents,
            movedComponent,
          ]),
        );

        if (currentPageId === draft.currentPageId) {
          draft.components = updatedCurrentComponents;
          draft.componentMap = toIdMap(updatedCurrentComponents);
          draft.selectedComponents = draft.selectedComponents.filter(
            (id) => id !== componentId,
          );
        }

        draft.isModified = true;
      });
    },

    copyComponentToPage: (componentId, targetPageId, position) => {
      const state = get();
      const component = state.getComponent(componentId);
      if (!component || !state.pageMap.get(targetPageId)) {
        return;
      }

      const targetPageComponents =
        state.pageComponents.get(targetPageId) || [];
      const basePosition = component.position ?? { x: 0, y: 0, z: 0 };
      const copiedComponent: Component = {
        ...component,
        id: generateId("component"),
        pageId: targetPageId,
        position: position || {
          x: basePosition.x + 20,
          y: basePosition.y + 20,
          z: basePosition.z ?? 0,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      set((draft) => {
        draft.pageComponents.set(
          targetPageId,
          enhanceComponentsWithChildren([
            ...targetPageComponents,
            copiedComponent,
          ]),
        );
        draft.isModified = true;
      });
    },

    getPage: (id) => get().pageMap.get(id),
    getAllPages: () => get().pages,
    getAllPagesWithComponents: () => {
      const state = get();

      return state.pages.map((page) => ({
        pageId: page.id,
        page,
        components:
          page.id === state.currentPageId
            ? state.components
            : state.pageComponents.get(page.id) || [],
      }));
    },

    syncCurrentPageComponents: () => {
      const state = get();
      if (!state.currentPageId) {
        return;
      }
      const currentPageId = state.currentPageId;

      set((draft) => {
        draft.pageComponents.set(currentPageId, [...state.components]);
      });
    },

    getPageCount: () => get().pages.length,
    isMultiPageProject: () => true,
    getDefaultPage: () => get().pages.find((page) => page.isDefault),
    canDeletePage: (pageId) => {
      const page = get().pageMap.get(pageId);
      return page ? !page.isDefault && get().pages.length > 1 : false;
    },
    getPageComponentCount: (pageId) =>
      (get().pageComponents.get(pageId) || []).length,
  };
}
